import React, { useState, useEffect } from 'react';
import { Card, Nav, Spinner, Alert } from 'react-bootstrap';
import './Documentation.css';

function Documentation() {
  const [activeDoc, setActiveDoc] = useState('changelog');
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const docs = {
    'changelog': {
      title: 'Changelog (2026-03-06)',
      file: '/docs/CHANGELOG-2026-03-06.md',
      description: 'Aktuelle Änderungen und Updates'
    },
    'admin': {
      title: 'Administrator-Handbuch',
      file: '/docs/ADMIN-HANDBUCH.md',
      description: 'Vollständiges Handbuch für System-Administratoren'
    },
    'features': {
      title: 'Feature-Dokumentation',
      file: '/docs/FEATURE-DOKUMENTATION.md',
      description: 'Detaillierte Feature-Übersicht und technische Details'
    },
    'production': {
      title: 'Production Deployment',
      file: '/docs/PRODUCTION-DEPLOYMENT.md',
      description: 'Deployment-Dokumentation und Server-Konfiguration'
    },
    'quick': {
      title: 'Quick Reference',
      file: '/docs/QUICK-REFERENCE.md',
      description: 'Schnellreferenz mit wichtigsten Befehlen'
    },
    'dns': {
      title: 'DNS-Lösung',
      file: '/docs/DNS-SOLUTION.md',
      description: 'Detaillierte DNS-Problemlösung'
    }
  };

  useEffect(() => {
    loadDocument(activeDoc);
  }, [activeDoc]);

  const loadDocument = async (docKey) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(docs[docKey].file);

      if (!response.ok) {
        throw new Error(`Dokument nicht gefunden: ${response.status}`);
      }

      const text = await response.text();
      setContent(text);
    } catch (err) {
      console.error('Fehler beim Laden der Dokumentation:', err);
      setError(err.message);
      setContent('');
    } finally {
      setLoading(false);
    }
  };

  // Einfacher Markdown-zu-HTML Converter (grundlegend)
  const renderMarkdown = (markdown) => {
    if (!markdown) return '';

    let html = markdown;

    // Code-Blöcke
    html = html.replace(/```(\w+)?\n([\s\S]*?)```/g, (match, lang, code) => {
      return `<pre><code class="language-${lang || 'text'}">${escapeHtml(code)}</code></pre>`;
    });

    // Inline-Code
    html = html.replace(/`([^`]+)`/g, '<code>$1</code>');

    // Überschriften
    html = html.replace(/^### (.+)$/gm, '<h3>$1</h3>');
    html = html.replace(/^## (.+)$/gm, '<h2>$1</h2>');
    html = html.replace(/^# (.+)$/gm, '<h1>$1</h1>');

    // Fett
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

    // Kursiv
    html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');

    // Links
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');

    // Listen (ungeordnet)
    html = html.replace(/^\- (.+)$/gm, '<li>$1</li>');
    html = html.replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>');

    // Listen (geordnet)
    html = html.replace(/^\d+\. (.+)$/gm, '<li>$1</li>');

    // Horizontale Linie
    html = html.replace(/^---$/gm, '<hr>');

    // Blockquotes
    html = html.replace(/^> (.+)$/gm, '<blockquote>$1</blockquote>');

    // Tabellen (einfach)
    html = html.replace(/^\|(.+)\|$/gm, (match, content) => {
      const cells = content.split('|').map(cell => cell.trim());
      const cellHtml = cells.map(cell => `<td>${cell}</td>`).join('');
      return `<tr>${cellHtml}</tr>`;
    });

    // Absätze
    html = html.split('\n\n').map(para => {
      if (para.startsWith('<') || para.trim() === '') {
        return para;
      }
      return `<p>${para}</p>`;
    }).join('\n');

    return html;
  };

  const escapeHtml = (text) => {
    const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    };
    return text.replace(/[&<>"']/g, m => map[m]);
  };

  const downloadDocument = () => {
    const doc = docs[activeDoc];
    const element = document.createElement('a');
    const file = new Blob([content], { type: 'text/markdown' });
    element.href = URL.createObjectURL(file);
    element.download = doc.file.split('/').pop();
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  return (
    <div className="documentation-page">
      <h1 className="mb-4">
        <i className="bi bi-book me-2"></i>
        Dokumentation
      </h1>

      <div className="row">
        {/* Sidebar Navigation */}
        <div className="col-md-3">
          <Card className="mb-4">
            <Card.Header className="bg-dark text-white">
              <strong>Dokumentationen</strong>
            </Card.Header>
            <Card.Body className="p-0">
              <Nav className="flex-column">
                {Object.entries(docs).map(([key, doc]) => (
                  <Nav.Link
                    key={key}
                    active={activeDoc === key}
                    onClick={() => setActiveDoc(key)}
                    className="doc-nav-item"
                  >
                    <div className="doc-nav-title">{doc.title}</div>
                    <small className="text-muted">{doc.description}</small>
                  </Nav.Link>
                ))}
              </Nav>
            </Card.Body>
          </Card>

          <Card className="info-card">
            <Card.Body>
              <h6 className="mb-3">
                <i className="bi bi-info-circle me-2"></i>
                Hinweis
              </h6>
              <p className="small mb-2">
                Alle Dokumentationen sind auch als Markdown-Dateien auf dem Server verfügbar:
              </p>
              <code className="small">/opt/dexios-monitoring/</code>
            </Card.Body>
          </Card>
        </div>

        {/* Main Content */}
        <div className="col-md-9">
          <Card>
            <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
              <div>
                <strong>{docs[activeDoc].title}</strong>
              </div>
              <button
                className="btn btn-sm btn-light"
                onClick={downloadDocument}
                disabled={!content}
              >
                <i className="bi bi-download me-1"></i>
                Download
              </button>
            </Card.Header>
            <Card.Body className="documentation-content">
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-3 text-muted">Lade Dokumentation...</p>
                </div>
              ) : error ? (
                <Alert variant="danger">
                  <Alert.Heading>Fehler beim Laden</Alert.Heading>
                  <p>{error}</p>
                  <hr />
                  <p className="mb-0">
                    <small>
                      Bitte stellen Sie sicher, dass die Dokumentations-Dateien
                      im <code>public/docs/</code> Verzeichnis vorhanden sind.
                    </small>
                  </p>
                </Alert>
              ) : content ? (
                <div
                  className="markdown-content"
                  dangerouslySetInnerHTML={{ __html: renderMarkdown(content) }}
                />
              ) : (
                <div className="text-center py-5 text-muted">
                  <i className="bi bi-file-earmark-text" style={{ fontSize: '3rem' }}></i>
                  <p className="mt-3">Wählen Sie eine Dokumentation aus der Seitenleiste</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default Documentation;
